import { X } from "lucide-react";
import { Select, type OptionType } from "@themes/pollinator/ui/Select";
import { Label } from "@themes/pollinator/ui/Label";
import { Button } from "@themes/pollinator/ui/Button";
import type { AffiliationEntry } from "@core/features/affiliations";

export type { AffiliationEntry };

export type Props = {
  /** Prefix for form field names */
  namePrefix: string;
  /** Current value */
  value?: AffiliationEntry;
  /** Change handler */
  onChange?: (value: AffiliationEntry) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Whether the field has an error */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Organization options for select */
  organizationOptions?: OptionType[];
  /** Role options for select */
  roleOptions?: OptionType[];
  /** Whether options are loading */
  isLoading?: boolean;
  /** Handler for creating new organization */
  onCreateOrganization?: (name: string) => void;
  /** Whether delete button should be shown */
  canDelete?: boolean;
  /** Handler for delete button click */
  onDelete?: () => void;
};

/**
 * Combined organization and role selection input.
 * A single row with organization (creatable) and role selects.
 */
export function AffiliationRow({
  namePrefix,
  value = {},
  onChange,
  onBlur,
  error,
  disabled,
  organizationOptions = [],
  roleOptions = [],
  isLoading = false,
  onCreateOrganization,
  canDelete = false,
  onDelete,
}: Props) {
  const handleOrganizationChange = (
    selected: OptionType | OptionType[] | null
  ) => {
    const singleValue = Array.isArray(selected)
      ? selected[0] ?? null
      : selected;
    onChange?.({ ...value, organization: singleValue });
  };

  const handleRoleChange = (selected: OptionType | OptionType[] | null) => {
    const singleValue = Array.isArray(selected)
      ? selected[0] ?? null
      : selected;
    onChange?.({ ...value, role: singleValue });
  };

  const handleCreateOrg = (inputValue: string) => {
    const newOption: OptionType = {
      label: inputValue,
      value: `new-${inputValue.toLowerCase().replace(/\s+/g, "-")}`,
      data: { isNew: true, name: inputValue },
    };
    onCreateOrganization?.(inputValue);
    onChange?.({ ...value, organization: newOption });
  };

  return (
    <div
      className="flex flex-col gap-4 md:flex-row md:gap-4 md:items-end"
      data-testid="affiliation-row"
    >
      <div className="flex-1 flex flex-col gap-1">
        <Label htmlFor={`${namePrefix}.organization`} id={`${namePrefix}.organization-label`}>Organization</Label>
        <Select
          name={`${namePrefix}.organization`}
          options={organizationOptions}
          value={value.organization ?? null}
          onChange={handleOrganizationChange}
          onBlur={onBlur}
          placeholder="Select or type organization..."
          isCreatable
          onCreate={handleCreateOrg}
          isLoading={isLoading}
          disabled={disabled}
          error={error}
          aria-labelledby={`${namePrefix}.organization-label`}
        />
      </div>

      <div className="flex-1 flex flex-col gap-1">
        <Label htmlFor={`${namePrefix}.role`} id={`${namePrefix}.role-label`}>Role</Label>
        <Select
          name={`${namePrefix}.role`}
          options={roleOptions}
          value={value.role ?? null}
          onChange={handleRoleChange}
          onBlur={onBlur}
          placeholder="Select role..."
          isLoading={isLoading}
          disabled={disabled}
          error={error}
          aria-labelledby={`${namePrefix}.role-label`}
        />
      </div>

      {canDelete && (
        <div className="flex-shrink-0 pb-0.5">
          <Button
            variant="ghost"
            iconComponent={X}
            onClick={onDelete}
            disabled={disabled}
            aria-label="Remove affiliation"
            data-testid="affiliation-row-delete-button"
          />
        </div>
      )}
    </div>
  );
}

export default AffiliationRow;
